<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-gray-900 bg-opacity-60 overflow-y-hidden h-full w-full flex items-center justify-center text-base z-999"
  >
    <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div
        class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"
      ></div>
    </div>
    <div
      v-else-if="visit && visit.patient"
      class="bg-white rounded-lg shadow-xl w-11/12 h-[88vh] overflow-hidden"
    >
      <div class="flex items-center justify-between p-4 border-b">
        <h2 class="text-2xl text-gray-800 font-semibold">
          {{ visit.VisitType === 'Procedure/Services' ? 'Prodedure/Service' : 'Consultation'}}
        </h2>
        <button
          @click="closeModal"
          class="text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <svg
            class="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            ></path>
          </svg>
        </button>
      </div>

      <div class="p-6 flex h-[75vh] overflow-y-auto">
        <!-- Left column -->
        <div class="w-2/12 pr-6 border-r">
          <div class="flex items-center mb-4">
            <img
              src="@/assets/images/avatar.png"
              alt="Patient"
              class="w-12 h-12 rounded-full mr-4"
            />
            <div>
              <h3 class="font-semibold">{{ visit?.patient.FullName }}</h3>
            </div>
          </div>
          <div class="space-y-2 py-2">
            <p class="flex flex-col">
              <span class="font-semibold">Unique Code</span>
              <span>{{ visit?.patient.unique_code }}</span>
            </p>
            <p>
              <span class="font-semibold">Email:</span>
              {{ visit?.patient.Email || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">Phone:</span>
              {{ visit?.patient.PhoneNumber || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">D.O.B:</span> {{ visit?.patient.Dob }}
            </p>
            <p>
              <span class="font-semibold">Gender:</span>
              {{ visit?.patient.Gender }}
            </p>
            <p>
              <span class="font-semibold">Emergency Contact:</span>
              {{ visit?.patient?.emergency_contact?.Name || "N/A" }}
            </p>
            <p>
              <span class="font-semibold">Phone:</span>
              {{ visit?.patient?.emergency_contact?.PhoneNumber || "N/A" }}
            </p>

            <div class="mt-4 mb-4 flex justify-center">
              <button
                v-if="state == 'create' || state == 'edit'"
                @click="handleMedicalHistory(visit?.patient?.PatientId)"
                class="px-4 py-1 bg-custom-yellow text-white rounded hover:bg-yellow-600"
              >
                Medical History
              </button>
            </div>
            <h4 class="font-semibold py-2 border-b-2 border-t-2 text-center">
              Current Vitals
            </h4>
          </div>
          <div class="grid grid-cols-3 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M10 13.5a4 4 0 1 0 4 0v-8.5a2 2 0 0 0 -4 0v8.5" />
                  <line x1="10" y1="9" x2="14" y2="9" />
                </svg>
              </span>
              <p class="text-xs">Temperature</p>
              <p class="font-medium text-base">{{ currentVitals.temperature }}°C</p>
            </div>

            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
              </span>
              <p class="text-xs">Heart Rate</p>
              <p class="font-medium text-base">{{ currentVitals.heart_rate ? currentVitals.heart_rate : "-" }}BPM</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="dark:custom-yellow text-xl">🫁</span>
              <p class="text-xs">Respiratory</p>
              <p class="font-medium text-base">{{ currentVitals.respiratory_rate ? currentVitals.respiratory_rate : "-" }}</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-xl">🩸</span>
              <p class="text-xs">Blood Pressure</p>
              <p class="font-medium text-base">{{ currentVitals.blood_pressure_systolic }} / {{ currentVitals.blood_pressure_diastolic }}</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <line x1="12" y1="3" x2="12" y2="21" />
                  <line x1="9" y1="21" x2="15" y2="21" />
                  <polyline points="3 6 6 7 12 5 18 7 21 6" />
                  <path d="M6 7l-3 9a5 5 0 0 0 6 0l-3 -9" />
                  <path d="M18 7l-3 9a5 5 0 0 0 6 0l-3 -9" />
                </svg>
              </span>
              <p class="text-xs">Weight</p>
              <p class="font-medium text-base">{{ currentVitals.weight }} KG</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M3 21h4l13 -13a1.5 1.5 0 0 0 -4 -4l-13 13v4" />
                  <line x1="14.5" y1="5.5" x2="18.5" y2="9.5" />
                  <polyline points="12 8 7 3 3 7 8 12" />
                  <line x1="7" y1="8" x2="5.5" y2="9.5" />
                  <polyline points="16 12 21 17 17 21 12 16" />
                  <line x1="16" y1="17" x2="14.5" y2="18.5" />
                </svg>
              </span>
              <p class="text-xs">Height</p>
              <p class="font-medium text-base">{{ currentVitals.height }} cm</p>
            </div>
          </div>

          <div class="mt-4 grid grid-cols-2 gap-4">
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <path d="M12 9v12m-8 -8a8 8 0 0 0 16 0m1 0h-2m-14 0h-2" />
                  <circle cx="12" cy="6" r="3" />
                </svg>
              </span>
              <p class="text-xs">BMI</p>
              <p class="font-medium text-base">{{ currentVitals.bmi }} KG/m2</p>
            </div>
            <div class="flex flex-col items-center justify-center text-center">
              <span class="text-custom-yellow text-2xl">
                <svg class="h-6 w-6" width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                  <path stroke="none" d="M0 0h24v24H0z" />
                  <circle cx="12" cy="5" r="2" />
                  <path d="M10 22v-5l-1-1v-4a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v4l-1 1v5" />
                </svg>
              </span>
              <p class="text-xs">BSA</p>
              <p class="font-medium text-base">{{ currentVitals.bma }} m2</p>
            </div>
          </div>
        </div>

        <!-- middle column -->
        <div v-if="isEnableMainDiv" class="w-2/12 pr-6 border-r">
          <ul class="mt-1 items-center justify-center">
            <li v-for="item in filteredMenuItems" :key="item.id" class="px-4 py-1">
              <a
                href="#"
                :class="[
                  'block px-4 py-1 rounded-md',
                  currentView === item.component
                    ? 'bg-custom-blue border-b-2 border-custom-yellow text-white font-semibold'
                    : 'text-gray-500 hover:bg-gray-100',
                ]"
                @click.prevent="setCurrentView(item.component)"
              >
                {{ item.title }}
              </a>
            </li>
          </ul>
        </div>

        <!-- Right column -->
        <div v-if="isEnableMainDiv" class="w-8/12 pl-6">
          <div class="flex mt-4 p-4 bg-red-300" v-if="errors.length">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>
          <keep-alive>
            <component
              :is="currentView"
              :state="state"
              :visitId="visitId"
              :ref="comp => {
                if (comp) {
                  if (currentView === 'TriageAssessment')       triageAssessmentInstance = comp;
                  if (currentView === 'PhysicalExam')           physicalExamInstance = comp;
                  if (currentView === 'PresentingComplaint')    presentingComplaintInstance = comp;
                  if (currentView === 'InvestigationProcedure') investigationProcedureInstance = comp;
                  if (currentView === 'ProcedureResults')       procedureResultsInstance = comp;
                  if (currentView === 'EncounterSummaryTab')    { /* read-only: no instance pull needed */ }
                  if (currentView === 'PatientDocumentsTab')    { /* read-only: no instance pull needed */ }
                  if (currentView === 'ExternalLabResultsTab')  { /* self-contained: no instance pull needed */ }
                  if (currentView === 'ExternalRadiologyResultsTab') { /* self-contained: no instance pull needed */ }
                  if (currentView === 'DiagnosisTab')           diagnosisTabInstance = comp;
                  if (currentView === 'PrescriptionTab')        prescriptionTabInstance = comp;
                  if (currentView === 'AppointmentScheduling')  appointmentSchedulingInstance = comp;
                }
              }"
              :triageAssessment="visit?.triage_assesment"
              :triageGlasgowComaScale="visit?.triage_glasgow_coma_scale"
              :drug_allergies="visit?.patient?.drug_allergies"
              :food_allergies="visit?.patient?.food_allergies"
              :drug_histories="visit?.patient?.drug_histories"
              :medical_histories="visit?.patient?.medical_histories"
              :family_histories="visit?.patient?.family_histories"
              :surgical_histories="visit?.patient?.surgical_histories"
              :triage_medication="visit?.patient?.triage_medication"
              :current_medication="visit?.patient?.current_medication"
              :gynae_history="visit?.gynae_history"
              :existingLabTests="existingLabTests"
              :existingRadiologyTests="existingRadiologyTests"
              :radiologyTests="existingRadiologyTests"
              :existingProcedures="existingProcedures"
              :existingPrescriptions="existingPrescriptions"
              :patientId="visit?.patient?.PatientId"
              :existingPhysicalExam="existingPhysicalExam"
              :existingComplaint="existingComplaint"
              :existingFindings="existingFindings"
              :existingPreliminaryDiagnosis="existingPreliminaryDiagnosis"
              :existingFinalDiagnosis="existingFinalDiagnosis"
              :existingSocialHistories="existingSocialHistories"
              :existingFamilyHistories="existingFamilyHistories"
              :patientGender="patientGender"
              :existingExternalPrescription="existingExternalPrescription"
              :visitDetails="prescriptionVisitDetails"
              :labTests="existingLabTests"
              @results-published="fetchConsultationVisitDetails"
            ></component>
          </keep-alive>
        </div>

        <!-- request scheduling -->
        <div v-if="!isEnableMainDiv" class="w-10/12 pr-6 border-r">
          <div class="tabs flex border-b mb-4 px-6">
            <span class="py-2 -mb-px font-semibold border-b-2 px-4 border-custom-yellow text-custom-blue">
              Schedule Request
            </span>
          </div>
          <div class="grid grid-cols-1 gap-4 max-h-[60vh] overflow-y-auto">
            <div class="flex flex-col px-6">
              <label class="font-semibold text-sm">
                <span class="text-red-500">* </span>Schedule Date
              </label>
              <div class="relative">
                <input
                  type="date"
                  v-model="testScheduling.date"
                  class="border-gray-300 rounded p-2 mt-1 bg-gray-50 w-full"
                />
              </div>
            </div>
            <div class="flex flex-col px-6">
              <label class="font-semibold text-sm">
                <span class="text-red-500">* </span>Schedule Time
              </label>
              <select v-model="testScheduling.time" class="border-gray-300 rounded p-2 mt-1 bg-gray-50">
                <option value="11:00 am" selected>11:00 am</option>
                <option value="12:00pm">12:00pm</option>
              </select>
            </div>
          </div>
          <div class="flex justify-end space-x-2 py-4 px-6">
            <button
              @click="handleSubmitScheduledRequest"
              class="px-8 py-1 bg-custom-yellow text-white rounded"
            >
              Submit
            </button>
          </div>
        </div>
      </div>

      <div class="flex justify-end space-x-2 p-4 border-t">
        <button
          @click="handlePreviousTab"
          class="px-8 py-1 bg-gray-200 text-gray-800 rounded"
        >
          Back
        </button>
        <template v-if="currentView === 'TriageAssessment'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'PhysicalExam'">
          <button @click="handlePhysicalExamSubmit" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'PresentingComplaint'">
          <button @click="handlePresentingComplaintFormSubmit" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'InvestigationProcedure'">
          <button @click="handleSubmitRequests" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'ProcedureResults'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'EncounterSummaryTab'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'PatientDocumentsTab'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'ExternalLabResultsTab'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'ExternalRadiologyResultsTab'">
          <button @click="handleNextTab" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'DiagnosisTab'">
          <button @click="handleSubmitDiagnosis" class="px-8 py-1 bg-custom-yellow text-white rounded">Next</button>
        </template>
        <template v-if="currentView === 'PrescriptionTab'">
          <button
            v-if="state !== 'view'"
            :disabled="showLoader"
            @click="handleIntermediateCloseVisit"
            class="px-8 py-1 bg-custom-blue text-white rounded"
          >
            {{ showLoader ? "Saving..." : "Save" }}
          </button>
        </template>
      </div>
    </div>

    <ConfirmationModal
      :show="showIntermediateCloseVisitConfirmation"
      :showLoader="showLoader"
      title="Save Consultation"
      message="Are you sure you want to save this consultation?"
      @cancel="closeIntermediateConfirmationModal"
      @confirm="submitConsultation"
    />

    <ConfirmationModal
      :show="showVisitCloseConfirmation"
      :showLoader="showCloseVisitLoader"
      title="Save & Close Visit"
      message="Are you sure you want to save and close this visit? This action cannot be undone."
      @cancel="closeConfirmationModal"
      @confirm="saveConsultationAndClose"
    />
  </div>
</template>

<script>
import { ref } from "vue";
import TriageAssessment       from "@/views/tabs/consultation_tabs/TriageAssessment.vue";
import PhysicalExam           from "@/views/tabs/consultation_tabs/PhysicalExam.vue";
import PresentingComplaint    from "@/views/tabs/consultation_tabs/PresentingComplaint.vue";
import InvestigationProcedure from "@/views/tabs/consultation_tabs/InvestigationProcedure.vue";
import ProcedureResults       from "@/views/tabs/consultation_tabs/ProcedureResults.vue";
import EncounterSummaryTab    from "@/views/tabs/consultation_tabs/EncounterSummaryTab.vue";
import PatientDocumentsTab    from "@/views/tabs/consultation_tabs/PatientDocumentsTab.vue";
import DiagnosisTab           from "@/views/tabs/consultation_tabs/DiagnosisTab.vue";
import PrescriptionTab        from "@/views/tabs/consultation_tabs/PrescriptionTab.vue";
import ExternalLabResultsTab  from "@/views/tabs/consultation_tabs/ExternalLabResultsTab.vue";
import ExternalRadiologyResultsTab from "@/views/tabs/consultation_tabs/ExternalRadiologyResultsTab.vue";
import ConfirmationModal      from "@/views/modals/shared/ConfirmationModal.vue";
import apiClient              from "@/services/api";
import { buildConsultationMenuItems } from "@/config/encounterTabs.js";

export default {
  name: "ConsultationPatientQueueModal",
  components: {
    TriageAssessment,
    PhysicalExam,
    PresentingComplaint,
    InvestigationProcedure,
    ProcedureResults,
    EncounterSummaryTab,
    PatientDocumentsTab,
    DiagnosisTab,
    PrescriptionTab,
    ConfirmationModal,
    ExternalLabResultsTab,
    ExternalRadiologyResultsTab,
  },

  setup() {
    const triageAssessmentInstance       = ref(null);
    const physicalExamInstance           = ref(null);
    const presentingComplaintInstance    = ref(null);
    const investigationProcedureInstance = ref(null);
    const procedureResultsInstance       = ref(null);
    const diagnosisTabInstance           = ref(null);
    const prescriptionTabInstance        = ref(null);
    const appointmentSchedulingInstance  = ref(null);

    const getTriageAssessmentPayload    = () => triageAssessmentInstance.value?.submitTriageAssessment?.()     || {};
    const getPhysicalExamPayload        = () => physicalExamInstance.value?.submitPhysicalExam?.()             || {};
    const getPresentingComplaintPayload = () => presentingComplaintInstance.value?.submitPresentingComplaint?.() || {};
    const getRequestsPayload            = () => investigationProcedureInstance.value?.submitAllRequests?.()    || {};
    const getProcedureResultsPayload    = () => procedureResultsInstance.value?.submitProcedureResults?.()     || {};
    const getDiagnosisPayload           = () => diagnosisTabInstance.value?.submitDiagnosis?.()                || {};
    const getPrescriptionPayload        = () => prescriptionTabInstance.value?.submitPrescriptions?.()         || [];
    const getAppointmentSchedulePayload = () => appointmentSchedulingInstance.value?.submitAppointmentSchedule?.() || {};

    return {
      triageAssessmentInstance,
      physicalExamInstance,
      presentingComplaintInstance,
      investigationProcedureInstance,
      procedureResultsInstance,
      diagnosisTabInstance,
      prescriptionTabInstance,
      appointmentSchedulingInstance,
      getTriageAssessmentPayload,
      getPhysicalExamPayload,
      getPresentingComplaintPayload,
      getRequestsPayload,
      getProcedureResultsPayload,
      getDiagnosisPayload,
      getPrescriptionPayload,
      getAppointmentSchedulePayload,
    };
  },

  props: {
    show:    { type: Boolean, required: true },
    visitId: { type: String,  required: true },
    state:   { type: String,  required: true },
  },

  data() {
    return {
      currentView: "TriageAssessment",
      isEnableMainDiv: true,
      showLoader: true,
      showCloseVisitLoader: false,
      showIntermediateCloseVisitConfirmation: false,
      visit: {},
      currentVitals: {
        temperature: "",
        heart_rate: "",
        respiratory_rate: "",
        blood_pressure_systolic: "",
        blood_pressure_diastolic: "",
        weight: "",
        height: "",
        bmi: "",
        bma: "",
      },
      testScheduling: {
        date: "",
        time: "",
      },
      payload: {
        close: false,
        physical_exam: {},
        diagnosis: [],
        social_history: [],
        family_history: [],     // ← added
        complaint: {},
        radiology: [],
        lab: [],
        findings: "",
        prescription: [],
        patient_id: null,
        visit_id: null,
      },
      existingLabTests: [],
      existingRadiologyTests: [],
      existingProcedures: [],
      existingPrescriptions: [],
      existingExternalPrescription: null,
      existingPhysicalExam: {},
      existingComplaint: {},
      existingFindings: "",
      existingPreliminaryDiagnosis: [],
      existingFinalDiagnosis: [],
      existingSocialHistories: [],
      existingFamilyHistories: [],   // ← added
      patientGender: "",
      showVisitCloseConfirmation: false,
      errors: [],
      facilityLogoUrl: null,
    };
  },

  watch: {
    visitId: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.fetchConsultationVisitDetails();
        }
      },
    },
  },

  computed: {
    currentViewRef() {
      return this.currentView;
    },
    showTriageAssessmentTab() {
      return this.visit.VisitType === 'Consultation';
    },
    menuItems() {
      // Full, unfiltered list — used by handleNextTab/handlePreviousTab traversal.
      return buildConsultationMenuItems({ showTriage: true });
    },
    filteredMenuItems() {
      // Sidebar list — Triage tab hidden for non-Consultation visit types.
      return buildConsultationMenuItems({ showTriage: this.showTriageAssessmentTab });
    },
    prescriptionVisitDetails() {
      return {
        visitDate:  this.visit?.VisitStartTime || null,
        prescriber: this.visit?.consultation?.doctor_name || "",
        patient:    this.visit?.patient || null,
        facility: {
          FacilityName: this.visit?.patient?.facility_name || "",
          NearestTown:  "",
          County:       "",
          logo_url:     this.facilityLogoUrl,
        },
      };
    },
  },

  methods: {
    async fetchConsultationVisitDetails() {
      try {
        this.showLoader = true;
        const response = await apiClient.getConsultationVisitDetails(this.visitId);

        if (response.success) {
          this.visit = response.data;

          if (this.state !== 'create') {
            this.currentView = 'EncounterSummaryTab';
          } else if (this.visit.VisitType !== 'Consultation') {
            this.currentView = 'PhysicalExam';
          }

          if (this.visit.patient?.latest_triage_vital) {
            this.currentVitals = {
              ...this.currentVitals,
              ...this.visit.patient.latest_triage_vital,
            };
          }
          if (this.visit.patient?.Gender) {
            this.patientGender = this.visit.patient.Gender;
          }
          this.handleExistingConsultationData(response.data);
          await this.fetchFacilityLogo();
        } else {
          this.$toast.error("Unable to fetch visit information.");
        }
      } catch (error) {
        this.$toast.error("Failed to fetch visit, please check your internet connection.");
      } finally {
        this.showLoader = false;
      }
    },

    async fetchFacilityLogo() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        if (!user?.FacilityId) return;
        const response = await apiClient.fetchFacility(user.FacilityId);
        if (response.success && response.data?.logo_url) {
          this.facilityLogoUrl = response.data.logo_url;
        }
      } catch {
        // Non-critical
      }
    },

    handleExistingConsultationData(data) {
      this.existingPhysicalExam         = data.physical_exam || {};
      this.existingComplaint            = data.complaint || {};
      this.existingLabTests             = data.lab_tests || [];
      this.existingRadiologyTests       = data.radiology_tests || [];
      this.existingProcedures           = data.procedures || [];
      this.existingPrescriptions        = data.prescriptions || [];
      this.existingExternalPrescription = data.external_prescription || null;
      this.existingFindings             = data.consultation?.findings || "";
      this.existingPreliminaryDiagnosis = data.diagnosis?.filter(d => d.diagnosis_type === 1) || [];
      this.existingFinalDiagnosis       = data.diagnosis?.filter(d => d.diagnosis_type === 2) || [];
      this.existingSocialHistories      = data.social_histories?.map(h => h.name) || [];
      this.existingFamilyHistories      = data.family_histories?.map(h => h.name) || [];  // ← added
    },

    handleMedicalHistory(patientId) {
      const routeUrl = this.$router.resolve({
        name: "ConsultationMedicalHistoryPage",
        params: { patient_id: patientId },
      }).href;
      window.open(routeUrl, "_blank");
    },

    closeModal() {
      this.isEnableMainDiv = true;
      this.$emit("close");
    },

    closeIntermediateConfirmationModal() {
      this.showIntermediateCloseVisitConfirmation = false;
    },

    handleIntermediateCloseVisit() {
      this.showIntermediateCloseVisitConfirmation = true;
    },

    handleRequestSchedule() {
      this.isEnableMainDiv = !this.isEnableMainDiv;
    },

    setCurrentView(component) {
      this.currentView = component;
    },

    handleNextTab() {
      this.isEnableMainDiv = true;
      const currentIndex = this.menuItems.findIndex(
        item => item.component === this.currentView
      );
      if (currentIndex < this.menuItems.length - 1) {
        this.currentView = this.menuItems[currentIndex + 1].component;
      } else {
        this.$emit("submit", this.visit?.patient?.PatientId);
      }
    },

    handlePreviousTab() {
      const currentIndex = this.menuItems.findIndex(
        item => item.component === this.currentView
      );
      if (currentIndex > 0) {
        this.currentView = this.menuItems[currentIndex - 1].component;
      }
    },

    handlePhysicalExamSubmit() {
      if (this.state === "create" || this.state === "edit") {
        const payload = this.getPhysicalExamPayload();
        if (this.physicalExamInstance?.validPhysicalExam?.() === false) {
          this.errors = ["Please complete all required fields in Physical Exam."];
          return;
        }
        this.payload.physical_exam = payload;
      }
      this.errors = [];
      this.handleNextTab();
    },

    handlePresentingComplaintFormSubmit() {
      if (this.state === "create" || this.state === "edit") {
        const payload = this.getPresentingComplaintPayload();
        if (this.presentingComplaintInstance?.validPresentingComplaint?.() === false) {
          this.errors = ["Please complete all required fields in Presenting Complaint."];
          return;
        }
        this.payload.complaint = payload;
        this.handleDiagnosisPayload(payload.diagnosis, 1);
        this.handleSocialHistoryPayload(payload.social);
        this.handleFamilyHistoryPayload(payload.family_history);  // ← added
      }
      this.errors = [];
      this.handleNextTab();
    },

    handleDiagnosisPayload(diagnoses, type) {
      if (diagnoses?.length) {
        this.payload.diagnosis = this.payload.diagnosis.filter(d => d.diagnosis_type !== type);
        diagnoses.forEach(diagnosis => {
          this.payload.diagnosis.push({
            diagnosis_id: diagnosis.id,
            diagnosis_type: type,
          });
        });
      }
    },

    handleSocialHistoryPayload(social_histories) {
      if (social_histories?.length) {
        this.payload.social_history = social_histories.map(history => ({ name: history }));
      }
    },

    handleFamilyHistoryPayload(family_histories) {  // ← added
      if (family_histories?.length) {
        this.payload.family_history = family_histories.map(item => ({ name: item }));
      }
    },

    handleSubmitRequests() {
      if (this.state === "create" || this.state === "edit") {
        const payload = this.getRequestsPayload();
        this.payload.radiology  = payload.radiologyRequests || [];
        this.payload.lab        = payload.labRequests || [];
        this.payload.procedure  = payload.procedureRequests || [];
      }
      this.handleNextTab();
    },

    handleSubmitDiagnosis() {
      if (this.state === "create" || this.state === "edit") {
        const payload = this.getDiagnosisPayload();
        this.handleDiagnosisPayload(payload.diagnosis, 2);
        this.payload.findings = payload.findings || "";
      }
      this.handleNextTab();
    },

    handleSubmitPrescription() {
      if (this.state === "create" || this.state === "edit") {
        this.payload.prescription = this.getPrescriptionPayload();
      }
    },

    handleSubmitScheduledRequest() {
      this.currentView     = "InvestigationProcedure";
      this.isEnableMainDiv = true;
    },

    closeConfirmationModal() {
      this.showVisitCloseConfirmation = false;
    },

    validateConsultation(closing) {
      this.errors = [];
      if (closing) {
        const preDiagnosis   = this.payload.diagnosis.filter(d => d.diagnosis_type === 1).length;
        const finalDiagnosis = this.payload.diagnosis.filter(d => d.diagnosis_type === 2).length;
        if (this.payload.diagnosis.length === 0) {
          this.errors.push("To close visit, add preliminary and final diagnosis.");
        } else {
          if (finalDiagnosis === 0) {
            this.errors.push("To close visit, add a final diagnosis in the diagnosis tab.");
          }
          if (preDiagnosis === 0) {
            this.errors.push("To close visit, add a preliminary diagnosis in the presenting complaint tab.");
          }
        }
      }
      return this.errors.length > 0;
    },

    preparePayload() {
      if (this.state === "create" || this.state === "edit") {
        this.payload.physical_exam = this.getPhysicalExamPayload();
        const complaintPayload     = this.getPresentingComplaintPayload();
        this.payload.complaint     = complaintPayload;
        this.handleDiagnosisPayload(complaintPayload.diagnosis, 1);
        this.handleSocialHistoryPayload(complaintPayload.social);
        this.handleFamilyHistoryPayload(complaintPayload.family_history);  // ← added
        const requestsPayload      = this.getRequestsPayload();
        this.payload.radiology  = requestsPayload.radiologyRequests || [];
        this.payload.lab        = requestsPayload.labRequests || [];
        this.payload.procedure  = requestsPayload.procedureRequests || [];
        const diagnosisPayload     = this.getDiagnosisPayload();
        this.handleDiagnosisPayload(diagnosisPayload.diagnosis, 2);
        this.payload.findings      = diagnosisPayload.findings || "";
        this.payload.prescription  = this.getPrescriptionPayload();
      }
    },

    async submitConsultation() {
      this.preparePayload();
      if (this.validateConsultation(false)) return;
      this.payload.patient_id = this.visit.patient?.PatientId;
      this.payload.visit_id   = this.visitId;
      try {
        this.showLoader = true;

        const response  = await apiClient.savePatientConsultation(this.payload);
        if (response.success) {

          if (response.data?.blocked_prescriptions.length) {
            response.data.blocked_prescriptions.forEach((bp) => this.$toast.error(bp.message))
            this.$toast.error('Some prescriptions were not saved due to allergy warnings. Please review the Prescriptions tab.')
            return;
          }
          this.$toast.success("Patient consultation saved successfully.");
          this.$emit("close");
          this.$emit("refetch");
        } else {
          this.$toast.error("Failed to save patient consultation.");
        }
      } catch (error) {
        this.$toast.error("Failed to save patient consultation.");
      } finally {
        this.showLoader = false;
      }
    },

    async saveConsultationAndClose() {
      this.preparePayload();
      if (this.validateConsultation(true)) return;
      this.payload.patient_id = this.visit.patient?.PatientId;
      this.payload.visit_id   = this.visitId;
      this.payload.close      = true;
      try {
        this.showCloseVisitLoader = true;
        const response = await apiClient.savePatientConsultation(this.payload);
        if (response.success) {
          this.$toast.success("Patient consultation saved and closed successfully.");
          this.showVisitCloseConfirmation = false;
          this.$emit("close");
          this.$emit("refetch");
        } else {
          this.$toast.error("Failed to save and close patient consultation.");
        }
      } catch (error) {
        this.$toast.error("Failed to save and close patient consultation.");
      } finally {
        this.showCloseVisitLoader = false;
      }
    },
  },
};
</script>